import Phaser from 'phaser';
import type { MapData } from '@shared/types/map';
import { TILE_SIZE, WORLD_TILES } from '@shared/constants';
import { Direction } from '@shared/types/entity';
import { getNPCTemplate } from '@shared/data/npcs';
import { isWaterSprite } from '@shared/utils/waterUtils';
import { getSurfaceById } from '../sprites/SurfaceSpriteManager';
import {
  preloadBodySprites,
  setupBodySpritesheets,
  createBodyAnimations,
  getBodySpriteKey,
  getBodyAnimKey,
  getBodyIdleFrame,
  WALK_ANIM_FRAME_RATE,
} from '../sprites/BodySpriteManager';
import {
  preloadHeadSprites,
  getHeadSpriteKey,
  getHeadFrameForDirection,
} from '../sprites/HeadSpriteManager';

/**
 * "Ciudad viva" del fondo del login: multitud AMBIENTE 100% client-side (sin ninguna
 * conexión al server → cero superficie de ataque, cero carga). NPCs de ciudad parados +
 * transeúntes que caminan CON RUMBO por las calles: navegan con BFS respetando bloqueos
 * (casas/paredes/árboles), agua y la ocupación de los demás (no se pisan), con velocidad y
 * timing propios (desincronizados) y movimiento continuo (no salto-pausa por tile). Reusa la
 * composición body+head de PlayerSyncController. Ver docs/AUTH_AND_LOGIN.md.
 */

// Composición (copiadas de GameScene para NO importar la escena pesada).
const BODY_OFFSET_Y = -26;
const BODY_FRAME_HEIGHT = 64;
const HEAD_OFFSET_FROM_BODY_BOTTOM = 27;
const HEAD_FRAME_HEIGHT = 30;
const HEAD_CENTER_ABOVE_FEET = HEAD_OFFSET_FROM_BODY_BOTTOM + HEAD_FRAME_HEIGHT / 2; // 42
const BODY_BOTTOM_FROM_CENTER = BODY_OFFSET_Y + BODY_FRAME_HEIGHT / 2; // 6

const TOWNSFOLK_BODIES = [1, 2, 3];
const TOWNSFOLK_HEADS = [1, 2, 3, 4, 5];

export interface AmbientCrowdConfig {
  maxPeople: number;
  /** Radio (tiles) del rectángulo navegable alrededor del centro. */
  radius: number;
}

interface Wanderer {
  state: 'idle' | 'walking';
  idleUntil: number;
  speed: number; // px/s (propia → desync)
  path: Array<{ x: number; y: number }>; // tiles restantes hasta el destino
  fx: number; // feet world x (centro del tile)
  fy: number; // feet world y (bottom del body)
  dir: Direction;
  reserved: string | null; // tile reservado hacia el que se mueve
}

interface CrowdEntity {
  body: Phaser.GameObjects.Sprite;
  head: Phaser.GameObjects.Sprite | null;
  bodyId: number;
  tileX: number;
  tileY: number;
  wander?: Wanderer;
}

const DIR_DELTA: Record<Direction, [number, number]> = {
  [Direction.UP]: [0, -1],
  [Direction.DOWN]: [0, 1],
  [Direction.LEFT]: [-1, 0],
  [Direction.RIGHT]: [1, 0],
};

function randInt(a: number, b: number): number {
  return a + Math.floor(Math.random() * (b - a + 1));
}
function key(x: number, y: number): string {
  return `${x},${y}`;
}
function dirFromDelta(dx: number, dy: number): Direction {
  if (Math.abs(dx) >= Math.abs(dy)) return dx >= 0 ? Direction.RIGHT : Direction.LEFT;
  return dy >= 0 ? Direction.DOWN : Direction.UP;
}

export class AmbientCrowd {
  private entities: CrowdEntity[] = [];
  private bodyIds = new Set<number>();
  private headIds = new Set<number>();
  private npcSpawns: Array<{ bodyId: number; headId: number; x: number; y: number }> = [];

  // Grillas de navegación (rect alrededor del centro).
  private blocked = new Set<string>(); // blocked del mapa + agua
  private roads: Array<{ x: number; y: number }> = []; // destinos con rumbo
  private occupied = new Set<string>(); // NPCs + tiles ocupados/reservados por transeúntes

  constructor(
    private scene: Phaser.Scene,
    private mapData: MapData,
    private centerX: number,
    private centerY: number,
    private cfg: AmbientCrowdConfig
  ) {
    this.collect();
  }

  private inRect(x: number, y: number): boolean {
    return Math.abs(x - this.centerX) <= this.cfg.radius && Math.abs(y - this.centerY) <= this.cfg.radius;
  }
  private walkable(x: number, y: number): boolean {
    return this.inRect(x, y) && !this.blocked.has(key(x, y));
  }

  private collect(): void {
    const R = this.cfg.radius;
    // NPCs de ciudad (amigables) cerca del centro → parados.
    for (const n of this.mapData.npcs ?? []) {
      if (Math.abs(n.x - this.centerX) > R || Math.abs(n.y - this.centerY) > R) continue;
      const tpl = getNPCTemplate(n.templateId);
      if (!tpl || tpl.hostile) continue;
      const bodyId = tpl.bodyId ?? 1;
      const headId = tpl.headId ?? 0;
      this.bodyIds.add(bodyId);
      if (headId > 0) this.headIds.add(headId);
      this.npcSpawns.push({ bodyId, headId, x: n.x, y: n.y });
    }
    for (const b of TOWNSFOLK_BODIES) this.bodyIds.add(b);
    for (const h of TOWNSFOLK_HEADS) this.headIds.add(h);

    // Bloqueos + caminos dentro del rect. IMPORTANTE: los bloqueos viven en la capa
    // INTERNA `_collision` (sprite 'blocked'), igual que MapSystem los lee de todas las
    // capas — por eso acá NO se saltean las capas `_` para el chequeo de bloqueo (antes se
    // salteaban y la gente atravesaba casas/paredes). Los caminos sí van sólo de capas reales.
    const roadSet = new Set<string>();
    for (const layer of this.mapData.layers) {
      const internal = layer.name.startsWith('_');
      for (const t of layer.tiles) {
        if (Math.abs(t.x - this.centerX) > R + 1 || Math.abs(t.y - this.centerY) > R + 1) continue;
        if (t.blocked === true || isWaterSprite(t.sprite)) this.blocked.add(key(t.x, t.y));
        else if (!internal && getSurfaceById(t.sprite)?.subtype === 'path') roadSet.add(key(t.x, t.y));
      }
    }
    // Caminos que quedaron walkable (no bloqueados por otra capa encima).
    for (const k of roadSet) {
      const [x, y] = k.split(',').map(Number);
      if (this.walkable(x, y)) this.roads.push({ x, y });
    }
  }

  public preloadSprites(): void {
    preloadBodySprites(this.scene, [...this.bodyIds]);
    preloadHeadSprites(this.scene, [...this.headIds]);
  }

  public create(): void {
    const ids = [...this.bodyIds];
    setupBodySpritesheets(this.scene, ids);
    createBodyAnimations(this.scene, ids, WALK_ANIM_FRAME_RATE);

    // NPCs estáticos (ocupan su tile).
    for (const s of this.npcSpawns) {
      this.spawn(s.bodyId, s.headId, s.x, s.y, false);
      this.occupied.add(key(s.x, s.y));
    }

    // Transeúntes: cantidad RANDOM entre 2 y maxPeople cada vez que carga el login.
    const now = this.scene.time.now;
    const count = randInt(2, Math.max(2, this.cfg.maxPeople));
    for (let i = 0; i < count; i++) {
      const start = this.freeWalkableTile();
      if (!start) break;
      const e = this.spawn(
        TOWNSFOLK_BODIES[randInt(0, TOWNSFOLK_BODIES.length - 1)],
        TOWNSFOLK_HEADS[randInt(0, TOWNSFOLK_HEADS.length - 1)],
        start.x,
        start.y,
        true
      );
      this.occupied.add(key(start.x, start.y));
      if (e?.wander) {
        // Velocidad natural del juego: 200ms/tile = 160 px/s (STEP_DURATION_MS). El MÍNIMO
        // es 160 (nunca más lento que un personaje real); varía sólo hacia arriba (desync).
        e.wander.speed = randInt(160, 184);
        e.wander.idleUntil = now + randInt(0, 3000); // arranque escalonado
      }
    }
  }

  private freeWalkableTile(): { x: number; y: number } | null {
    for (let tries = 0; tries < 40; tries++) {
      const x = this.centerX + randInt(-this.cfg.radius, this.cfg.radius);
      const y = this.centerY + randInt(-this.cfg.radius, this.cfg.radius);
      if (this.walkable(x, y) && !this.occupied.has(key(x, y))) return { x, y };
    }
    return null;
  }

  private spawn(
    bodyId: number,
    headId: number,
    tileX: number,
    tileY: number,
    wandering: boolean
  ): CrowdEntity | null {
    if (!this.scene.textures.exists(getBodySpriteKey(bodyId))) return null;
    const fx = tileX * TILE_SIZE + TILE_SIZE / 2;
    const fy = tileY * TILE_SIZE + TILE_SIZE / 2 + BODY_BOTTOM_FROM_CENTER;

    const body = this.scene.add.sprite(fx, fy, getBodySpriteKey(bodyId)).setOrigin(0.5, 1);
    body.setFrame(getBodyIdleFrame(Direction.DOWN, bodyId));
    body.setDepth(this.depthFor(tileY));

    let head: Phaser.GameObjects.Sprite | null = null;
    if (headId > 0 && this.scene.textures.exists(getHeadSpriteKey(headId))) {
      head = this.scene.add
        .sprite(fx, fy - HEAD_CENTER_ABOVE_FEET, getHeadSpriteKey(headId))
        .setOrigin(0.5, 0.5);
      head.setFrame(getHeadFrameForDirection(Direction.DOWN));
      head.setDepth(this.depthFor(tileY) + 0.1);
    }

    const ent: CrowdEntity = { body, head, bodyId, tileX, tileY };
    if (wandering) {
      ent.wander = {
        state: 'idle',
        idleUntil: this.scene.time.now + randInt(400, 2500),
        speed: 50,
        path: [],
        fx,
        fy,
        dir: Direction.DOWN,
        reserved: null,
      };
    }
    this.entities.push(ent);
    return ent;
  }

  public update(deltaMs: number): void {
    const now = this.scene.time.now;
    for (const e of this.entities) {
      const w = e.wander;
      if (!w) continue;

      if (w.state === 'idle') {
        if (now >= w.idleUntil) this.planPath(e);
        continue;
      }

      // Mover CONTINUO hacia el próximo nodo del path.
      const target = w.path[0];
      if (!target) {
        this.arriveIdle(e, now);
        continue;
      }
      const tx = target.x * TILE_SIZE + TILE_SIZE / 2;
      const ty = target.y * TILE_SIZE + TILE_SIZE / 2 + BODY_BOTTOM_FROM_CENTER;
      const dx = tx - w.fx;
      const dy = ty - w.fy;
      const dist = Math.hypot(dx, dy);
      const stepPx = (w.speed * deltaMs) / 1000;

      if (dist <= stepPx || dist < 0.5) {
        // Llegó al tile: liberar el anterior, consolidar el nuevo.
        this.occupied.delete(key(e.tileX, e.tileY));
        e.tileX = target.x;
        e.tileY = target.y;
        w.fx = tx;
        w.fy = ty;
        w.path.shift();
        w.reserved = null;
        // Reservar el siguiente (si hay y está libre); si no, terminar/esperar.
        const nxt = w.path[0];
        if (nxt && !this.occupiedByOther(nxt.x, nxt.y, e)) {
          this.occupied.add(key(nxt.x, nxt.y));
          w.reserved = key(nxt.x, nxt.y);
        } else if (nxt) {
          w.path = []; // bloqueado por otro → replanear en el próximo idle
        }
      } else {
        w.fx += (dx / dist) * stepPx;
        w.fy += (dy / dist) * stepPx;
        const nd = dirFromDelta(dx, dy);
        if (nd !== w.dir) {
          w.dir = nd;
          e.body.anims.play(getBodyAnimKey(e.bodyId, nd), true);
          if (e.head) e.head.setFrame(getHeadFrameForDirection(nd));
        } else if (!e.body.anims.isPlaying) {
          e.body.anims.play(getBodyAnimKey(e.bodyId, nd), true);
        }
      }

      // Aplicar posición + depth por fila actual.
      e.body.setPosition(w.fx, w.fy);
      if (e.head) e.head.setPosition(w.fx, w.fy - HEAD_CENTER_ABOVE_FEET);
      const row = Math.round((w.fy - BODY_BOTTOM_FROM_CENTER - TILE_SIZE / 2) / TILE_SIZE);
      e.body.setDepth(this.depthFor(row));
      if (e.head) e.head.setDepth(this.depthFor(row) + 0.1);

      if (w.path.length === 0) this.arriveIdle(e, now);
    }
  }

  private arriveIdle(e: CrowdEntity, now: number): void {
    const w = e.wander!;
    if (w.reserved) {
      this.occupied.delete(w.reserved);
      w.reserved = null;
    }
    w.state = 'idle';
    w.idleUntil = now + randInt(600, 3500);
    e.body.anims.stop();
    e.body.setFrame(getBodyIdleFrame(w.dir, e.bodyId));
  }

  /** Elige un destino (camino preferido) y arma el path por BFS; si no, sigue idle. */
  private planPath(e: CrowdEntity): void {
    const w = e.wander!;
    const dest = this.pickDestination(e);
    if (!dest) {
      w.idleUntil = this.scene.time.now + randInt(500, 1500);
      return;
    }
    const path = this.bfs(e.tileX, e.tileY, dest.x, dest.y, e);
    if (!path || path.length === 0) {
      w.idleUntil = this.scene.time.now + randInt(500, 1500);
      return;
    }
    const first = path[0];
    if (this.occupiedByOther(first.x, first.y, e)) {
      w.idleUntil = this.scene.time.now + randInt(300, 900);
      return;
    }
    this.occupied.add(key(first.x, first.y));
    w.reserved = key(first.x, first.y);
    w.path = path;
    w.state = 'walking';
    w.dir = dirFromDelta(first.x - e.tileX, first.y - e.tileY);
    e.body.anims.play(getBodyAnimKey(e.bodyId, w.dir), true);
    if (e.head) e.head.setFrame(getHeadFrameForDirection(w.dir));
  }

  /** Un tile de camino al alcance (fallback: walkable), libre. */
  private pickDestination(e: CrowdEntity): { x: number; y: number } | null {
    const REACH = 12;
    const pool = this.roads.length > 0 ? this.roads : null;
    for (let tries = 0; tries < 24; tries++) {
      let x: number, y: number;
      if (pool) {
        const r = pool[randInt(0, pool.length - 1)];
        x = r.x;
        y = r.y;
      } else {
        x = this.centerX + randInt(-this.cfg.radius, this.cfg.radius);
        y = this.centerY + randInt(-this.cfg.radius, this.cfg.radius);
      }
      if (Math.abs(x - e.tileX) + Math.abs(y - e.tileY) > REACH) continue;
      if ((x === e.tileX && y === e.tileY) || !this.walkable(x, y) || this.occupiedByOther(x, y, e)) continue;
      return { x, y };
    }
    return null;
  }

  private occupiedByOther(x: number, y: number, self: CrowdEntity): boolean {
    if (x === self.tileX && y === self.tileY) return false;
    return this.occupied.has(key(x, y));
  }

  /** BFS 4-dir sobre walkable menos ocupados (excepto el tile propio). Devuelve el path sin el inicio. */
  private bfs(
    sx: number,
    sy: number,
    gx: number,
    gy: number,
    self: CrowdEntity
  ): Array<{ x: number; y: number }> | null {
    if (sx === gx && sy === gy) return [];
    const start = key(sx, sy);
    const prev = new Map<string, string>();
    const seen = new Set<string>([start]);
    let queue: Array<[number, number]> = [[sx, sy]];
    let nodes = 0;
    const MAX_NODES = 1600;
    while (queue.length > 0) {
      const next: Array<[number, number]> = [];
      for (const [cx, cy] of queue) {
        if (++nodes > MAX_NODES) return null;
        for (const d of [Direction.UP, Direction.DOWN, Direction.LEFT, Direction.RIGHT]) {
          const [dx, dy] = DIR_DELTA[d];
          const nx = cx + dx;
          const ny = cy + dy;
          const nk = key(nx, ny);
          if (seen.has(nk)) continue;
          if (!this.walkable(nx, ny)) continue;
          const isGoal = nx === gx && ny === gy;
          if (!isGoal && this.occupiedByOther(nx, ny, self)) continue;
          seen.add(nk);
          prev.set(nk, key(cx, cy));
          if (isGoal) {
            const path: Array<{ x: number; y: number }> = [];
            let k: string | undefined = nk;
            while (k && k !== start) {
              const [px, py] = k.split(',').map(Number);
              path.unshift({ x: px, y: py });
              k = prev.get(k);
            }
            return path;
          }
          next.push([nx, ny]);
        }
      }
      queue = next;
    }
    return null;
  }

  private depthFor(tileY: number): number {
    const clamped = Phaser.Math.Clamp(tileY, 0, WORLD_TILES - 1);
    return (clamped / WORLD_TILES) * 700 + 0.5;
  }

  public destroy(): void {
    for (const e of this.entities) {
      e.body.destroy();
      e.head?.destroy();
    }
    this.entities = [];
    this.occupied.clear();
  }
}
